import { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import { TrailerModal } from './TrailerModal';
import { Skeleton } from '@/components/ui/skeleton';

interface FeaturedVideo {
  id: string;
  title: string;
  subtitle: string | null;
  thumbnail_url: string | null;
  vimeo_id: string;
  sort_order: number;
}

function VideoCardSkeleton() {
  return (
    <div className="animate-pulse flex-shrink-0 w-[85vw] md:w-[calc(50%-12px)]">
      <div className="aspect-video bg-white/5 rounded-t-[10px]" />
      <div
        className="p-4"
        style={{
          background: 'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(0,0,0,0.1) 100%)',
          borderRadius: '0 0 10px 10px',
        }}
      >
        <Skeleton className="h-5 w-3/4 mb-2 bg-white/10" />
        <Skeleton className="h-4 w-1/2 bg-white/5" />
      </div>
    </div>
  );
}

export function FeaturedVideosSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [videos, setVideos] = useState<FeaturedVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [sliderIndex, setSliderIndex] = useState(0);
  const [isDesktop, setIsDesktop] = useState(false);

  const [trailerModalOpen, setTrailerModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<{ vimeoId: string; title: string } | null>(null);

  const itemsPerView = 2;
  const maxSliderIndex = Math.max(0, Math.ceil(videos.length / itemsPerView) - 1);
  const canSlideLeft = sliderIndex > 0;
  const canSlideRight = sliderIndex < maxSliderIndex;

  useEffect(() => {
    const checkDesktop = () => setIsDesktop(window.innerWidth >= 768);
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  useEffect(() => {
    if (loading || videos.length === 0) return;
    const element = sectionRef.current;
    if (!element) return;

    const rect = element.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.8) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [loading, videos.length]);

  useEffect(() => {
    const fetchVideos = async () => {
      const { data } = await supabase
        .from('homepage_featured_videos')
        .select('*')
        .order('sort_order', { ascending: true });

      if (data) setVideos(data);
      setLoading(false);
    };
    fetchVideos();
  }, []);

  if (!loading && videos.length === 0) return null;

  return (
    <section className="pb-8 md:pb-16 lg:pb-24 px-4 sm:px-6 lg:px-8 bg-background">
      <div
        ref={sectionRef}
        className="max-w-6xl mx-auto"
      >
        {/* Header */}
        <div
          className={cn(
            'relative mb-6 md:mb-10 transition-all duration-600',
            isVisible && !loading ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
          )}
        >
          <h2 className="text-hero-smaller leading-tight text-foreground">
            HEAR A SAMPLE
          </h2>
        </div>

        {/* Video Slider */}
        <div className="relative overflow-hidden min-h-[280px]">
          <div
            className="flex gap-6 transition-transform duration-500 ease-out overflow-x-auto md:overflow-visible snap-x snap-mandatory md:snap-none scrollbar-hide pb-4 md:pb-0"
            style={
              isDesktop
                ? { transform: `translateX(-${sliderIndex * (100 / itemsPerView + 3)}%)` }
                : undefined
            }
          >
            {loading ? (
              <>
                <VideoCardSkeleton />
                <VideoCardSkeleton />
              </>
            ) : (
              videos.map((v, index) => (
                <div
                  key={v.id}
                  className={cn(
                    'flex-shrink-0 w-[85vw] md:w-[calc(50%-12px)] snap-start transition-opacity duration-500',
                    isVisible ? 'opacity-100' : 'opacity-0'
                  )}
                  style={{
                    transitionDelay: isVisible ? `${400 + (index % 2) * 100}ms` : '0ms',
                  }}
                >
                  <div
                    onClick={() => {
                      setSelectedVideo({ vimeoId: v.vimeo_id, title: v.title });
                      setTrailerModalOpen(true);
                    }}
                    className="group relative rounded-[10px] overflow-hidden cursor-pointer"
                  >
                    {/* Thumbnail */}
                    <div className="relative aspect-video overflow-hidden">
                      {v.thumbnail_url ? (
                        <>
                          <img
                            src={v.thumbnail_url}
                            alt={v.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                          <div
                            className="absolute inset-0 pointer-events-none"
                            style={{
                              background:
                                'linear-gradient(180deg, rgba(0,0,0,0) 67.84%, rgba(0,0,0,0.8) 107.45%)',
                            }}
                          />
                        </>
                      ) : (
                        <div className="w-full h-full bg-muted flex items-center justify-center">
                          <span className="text-foreground">No thumbnail</span>
                        </div>
                      )}

                      {/* Play badge */}
                      <div className="absolute bottom-3 left-3 flex items-center gap-2 bg-black/60 backdrop-blur-sm px-3 py-1.5 rounded-full group-hover:bg-black/80 transition-colors duration-200 z-10">
                        <Play className="w-3 h-3 text-foreground fill-current" />
                        <span className="text-xs text-foreground font-medium">Watch Video</span>
                      </div>
                    </div>

                    {/* Info box */}
                    <div
                      className="p-4"
                      style={{
                        background:
                          'linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(0,0,0,0.1) 100%)',
                        borderRadius: '0 0 10px 10px',
                      }}
                    >
                      <h3 className="text-video-title text-white mb-1 line-clamp-2">{v.title}</h3>
                      {v.subtitle && (
                        <p className="text-sm text-muted-foreground">{v.subtitle}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Navigation Arrows (desktop) */}
        {videos.length > itemsPerView && (
          <div
            className={cn(
              'hidden md:flex justify-center gap-4 mt-4 transition-opacity duration-500',
              isVisible && !loading ? 'opacity-100' : 'opacity-0'
            )}
            style={{ transitionDelay: isVisible ? '700ms' : '0ms' }}
          >
            <button
              onClick={() => setSliderIndex((p) => Math.max(p - 1, 0))}
              disabled={!canSlideLeft}
              className={cn(
                'w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200',
                canSlideLeft
                  ? 'bg-white hover:bg-white/80'
                  : 'bg-transparent border border-white/50'
              )}
              aria-label="Previous"
            >
              <ChevronLeft
                className={cn(
                  'w-5 h-5 transition-colors duration-200',
                  canSlideLeft ? 'text-destructive' : 'text-white/50'
                )}
              />
            </button>
            <button
              onClick={() => setSliderIndex((p) => Math.min(p + 1, maxSliderIndex))}
              disabled={!canSlideRight}
              className={cn(
                'w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200',
                canSlideRight
                  ? 'bg-white hover:bg-white/80'
                  : 'bg-transparent border border-white/50'
              )}
              aria-label="Next"
            >
              <ChevronRight
                className={cn(
                  'w-5 h-5 transition-colors duration-200',
                  canSlideRight ? 'text-destructive' : 'text-white/50'
                )}
              />
            </button>
          </div>
        )}
      </div>

      <TrailerModal
        isOpen={trailerModalOpen}
        onClose={() => {
          setTrailerModalOpen(false);
          setSelectedVideo(null);
        }}
        vimeoId={selectedVideo?.vimeoId || null}
        title={selectedVideo?.title || ''}
      />
    </section>
  );
}
